import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, Pencil, Trash2, Wand2, Loader2, Search, UtensilsCrossed, ImagePlus, X as XIcon, AlertTriangle, Package, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const categories = [
  { value: 'main', label: 'Main Dish', labelAr: 'طبق رئيسي' },
  { value: 'appetizer', label: 'Appetizer', labelAr: 'مقبلات' },
  { value: 'dessert', label: 'Dessert', labelAr: 'حلويات' },
  { value: 'beverage', label: 'Beverage', labelAr: 'مشروبات' },
  { value: 'side', label: 'Side', labelAr: 'أطباق جانبية' },
];

const emptyForm = {
  name: '', name_ar: '', description: '', description_ar: '',
  price: '', category: 'main', inventory_count: 100, image_url: ''
};

export default function MenuPage() {
  const { activeBranch, authHeaders, API } = useAuth();
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [inventoryAlerts, setInventoryAlerts] = useState({ low: 0, out: 0 });
  const [generatingImageFor, setGeneratingImageFor] = useState(null);
  const [bulkRegenerating, setBulkRegenerating] = useState(false);

  const missingImagesCount = items.filter(i => !i.image_url || i.image_url.startsWith('/api/uploads/generated/')).length;

  const handleBulkRegenerateImages = async () => {
    if (!activeBranch) return;
    if (!window.confirm(t('menu.bulkRegenConfirm', { count: missingImagesCount }))) return;
    setBulkRegenerating(true);
    try {
      const res = await axios.post(
        `${API}/menu-items/regenerate-missing-images?branch_id=${activeBranch.id}&max_items=30`,
        {},
        authHeaders()
      );
      const { regenerated, failed, quota_exhausted, remaining } = res.data;
      if (quota_exhausted) {
        toast.warning(t('menu.bulkRegenPartial', { done: regenerated }));
      } else if (regenerated > 0) {
        toast.success(t('menu.bulkRegenSuccess', { done: regenerated, failed: failed || 0 }));
        if (remaining > 0) {
          toast.info(t('menu.bulkRegenRemaining', { count: remaining }));
        }
      } else {
        toast.info(t('menu.bulkRegenNone'));
      }
      fetchItems();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('menu.bulkRegenFailed'));
    } finally {
      setBulkRegenerating(false);
    }
  };

  useEffect(() => { fetchItems(); fetchInventoryAlerts(); }, [activeBranch]);

  const fetchInventoryAlerts = async () => {
    if (!activeBranch) return;
    try {
      const res = await axios.get(`${API}/inventory/alerts?branch_id=${activeBranch.id}`, authHeaders());
      const alerts = res.data.alerts || [];
      setInventoryAlerts({
        low: alerts.filter(a => a.type === 'low_stock').length,
        out: alerts.filter(a => a.type === 'out_of_stock').length,
      });
    } catch { /* silent */ }
  };

  const fetchItems = async () => {
    if (!activeBranch) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/menu-items?branch_id=${activeBranch.id}`, authHeaders());
      setItems(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t('menu.imageTooLarge')); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/builder/upload-image`, fd, {
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      // Store relative URL so it works across environments (preview/production)
      setForm(prev => ({ ...prev, image_url: res.data.url }));
      toast.success(t('menu.imageUploaded'));
    } catch { toast.error(t('menu.imageUploadFailed')); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) { toast.error(t('menu.nameRequired')); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/menu-items/${editingId}`, { ...form, price: parseFloat(form.price), inventory_count: parseInt(form.inventory_count) }, authHeaders());
        toast.success(t('menu.itemUpdated'));
      } else {
        await axios.post(`${API}/menu-items`, { ...form, branch_id: activeBranch.id, price: parseFloat(form.price), inventory_count: parseInt(form.inventory_count) }, authHeaders());
        toast.success(t('menu.itemCreated'));
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      fetchItems();
    } catch (err) { toast.error(t('menu.saveFailed')); }
    finally { setSaving(false); }
  };

  const handleEdit = (item) => {
    setForm({
      name: item.name, name_ar: item.name_ar, description: item.description,
      description_ar: item.description_ar, price: item.price, category: item.category,
      inventory_count: item.inventory_count, image_url: item.image_url || ''
    });
    setEditingId(item.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('menu.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/menu-items/${id}`, authHeaders());
      toast.success(t('menu.itemDeleted'));
      fetchItems();
    } catch (err) { toast.error(t('menu.deleteFailed')); }
  };

  const handleAIGenerate = async () => {
    if (!form.name) { toast.error(t('menu.nameFirst')); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/ai/generate-description`, { item_name: form.name, category: form.category }, authHeaders());
      if (res.data.success && res.data.data) {
        setForm(prev => ({
          ...prev,
          description: res.data.data.description_en || prev.description,
          description_ar: res.data.data.description_ar || prev.description_ar
        }));
        toast.success(t('menu.aiGenerated'));
      }
    } catch (err) { toast.error(t('menu.aiFailed')); }
    finally { setGenerating(false); }
  };

  const handleAIGenerateImage = async (itemId) => {
    setGeneratingImageFor(itemId);
    try {
      const res = await axios.post(`${API}/menu-items/${itemId}/generate-image`, {}, authHeaders());
      if (res.data.success) {
        toast.success(t('menu.aiImageGenerated'));
        fetchItems();
      }
    } catch (err) {
      // Surface the real backend reason (quota / feature gating) rather than a generic failure.
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 429 && detail && typeof detail === 'object') {
        const reason = detail.reason;
        const plan = detail.plan || 'trial';
        if (reason === 'feature_not_available') {
          toast.error(`توليد الصور غير متاح في خطتك (${plan}). يرجى الترقية لخطة Pro.`);
        } else if (reason === 'quota_exceeded') {
          toast.error(`تم استنفاد حصة توليد الصور (${detail.current}/${detail.limit}). يرجى الترقية.`);
        } else {
          toast.error(detail.message || t('menu.aiImageFailed'));
        }
      } else {
        toast.error(t('menu.aiImageFailed'));
      }
    } finally {
      setGeneratingImageFor(null);
    }
  };

  const handleAIGenerateImageForForm = async () => {
    if (!form.name && !form.name_ar) { toast.error(t('menu.nameFirst')); return; }
    setUploading(true);
    try {
      const name = form.name_ar || form.name;
      const res = await axios.post(`${API}/ai/generate-food-image`, {
        name, category: form.category
      }, authHeaders());
      if (res.data.success && res.data.image_url) {
        // Store relative URL - resolves to whichever origin (preview/production)
        setForm(prev => ({ ...prev, image_url: res.data.image_url }));
        toast.success(t('menu.aiImageGenerated'));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 429 && detail && typeof detail === 'object') {
        const reason = detail.reason;
        const plan = detail.plan || 'trial';
        if (reason === 'feature_not_available') {
          toast.error(`توليد الصور غير متاح في خطتك (${plan}). يرجى الترقية لخطة Pro.`);
        } else if (reason === 'quota_exceeded') {
          toast.error(`تم استنفاد حصة توليد الصور (${detail.current}/${detail.limit}). يرجى الترقية.`);
        } else {
          toast.error(detail.message || t('menu.aiImageFailed'));
        }
      } else {
        toast.error(t('menu.aiImageFailed'));
      }
    } finally {
      setUploading(false);
    }
  };

  const filtered = items.filter(item => {
    const matchSearch = item.name.toLowerCase().includes(search.toLowerCase()) || item.name_ar?.includes(search);
    const matchCat = filterCat === 'all' || item.category === filterCat;
    return matchSearch && matchCat;
  });

  const catLabel = (cat) => {
    const key = `menu.${cat}`;
    return t(key) || cat;
  };

  return (
    <ModuleLayout moduleKey="operations">
      <div className="space-y-6" data-testid="menu-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-cairo font-bold text-2xl text-foreground">{t('menu.title')}</h1>
            <p className="text-sm text-muted-foreground">{items.length} {t('menu.totalItems')} {filterCat !== 'all' && `• ${catLabel(filterCat)}`}</p>
          </div>
          <div className="flex items-center gap-2">
            {missingImagesCount > 0 && (
              <Button
                variant="outline"
                onClick={handleBulkRegenerateImages}
                disabled={bulkRegenerating}
                data-testid="bulk-regen-images-btn"
                className="rounded-full gap-2 border-primary/30 text-primary hover:bg-primary/5"
              >
                {bulkRegenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {t('menu.regenMissingImages', { count: missingImagesCount })}
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setForm(emptyForm); setEditingId(null); } }}>
              <DialogTrigger asChild>
                <Button className="rounded-full" data-testid="add-menu-item-btn">
                  <Plus className="w-4 h-4 mr-2" /> {t('menu.addItem')}
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-cairo">{editingId ? t('menu.editItem') : t('menu.newItem')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSave} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm mb-1.5 block">{t('menu.nameEn')}</Label>
                    <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="menu-name-input" placeholder="Chicken Kabsa" />
                  </div>
                  <div>
                    <Label className="text-sm mb-1.5 block">{t('menu.nameAr')}</Label>
                    <Input value={form.name_ar} onChange={e => setForm({...form, name_ar: e.target.value})} data-testid="menu-name-ar-input" dir="rtl" placeholder="كبسة دجاج" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm mb-1.5 block">{t('menu.priceSAR')}</Label>
                    <Input type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} data-testid="menu-price-input" placeholder="45" />
                  </div>
                  <div>
                    <Label className="text-sm mb-1.5 block">{t('menu.category')}</Label>
                    <Select value={form.category} onValueChange={val => setForm({...form, category: val})}>
                      <SelectTrigger data-testid="menu-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categories.map(c => <SelectItem key={c.value} value={c.value}>{t(`menu.${c.value}`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">{t('menu.inventoryCount')}</Label>
                  <Input type="number" value={form.inventory_count} onChange={e => setForm({...form, inventory_count: e.target.value})} data-testid="menu-inventory-input" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">{t('menu.description')}</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={handleAIGenerate} disabled={generating} data-testid="ai-generate-desc-btn" className="text-primary text-xs gap-1">
                    {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                    {t('menu.aiGenerate')}
                  </Button>
                </div>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="English description" rows={2} data-testid="menu-desc-input" />
                <Textarea value={form.description_ar} onChange={e => setForm({...form, description_ar: e.target.value})} placeholder="الوصف بالعربية" rows={2} dir="rtl" data-testid="menu-desc-ar-input" />
                {/* Image Upload */}
                <div>
                  <Label className="text-sm mb-1.5 block">{t('menu.image')}</Label>
                  {form.image_url ? (
                    <div className="relative rounded-lg overflow-hidden border border-border/50" style={{ height: 120 }}>
                      <img src={form.image_url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                      <button type="button" onClick={() => setForm({...form, image_url: ''})}
                        className="absolute top-2 left-2 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80" data-testid="menu-remove-image">
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="flex flex-col items-center justify-center h-20 rounded-lg border-2 border-dashed border-border/50 cursor-pointer hover:border-primary/40 transition-colors" data-testid="menu-image-upload-area">
                        <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="menu-image-input" />
                        {uploading ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : <ImagePlus className="w-5 h-5 text-muted-foreground" />}
                        <span className="text-xs text-muted-foreground mt-1">{uploading ? t('menu.uploading') : t('menu.uploadImage')}</span>
                      </label>
                      <Button type="button" variant="outline" size="sm" onClick={handleAIGenerateImageForForm} disabled={uploading} data-testid="ai-gen-image-form-btn"
                        className="w-full text-xs gap-1.5 rounded-full border-primary/20 text-primary hover:bg-primary/5">
                        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        {t('menu.aiGenerateImage')}
                      </Button>
                    </div>
                  )}
                </div>
                <Button type="submit" disabled={saving} className="w-full rounded-full" data-testid="menu-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? t('common.update') : t('common.create'))}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Cross-section inventory alert */}
        {(inventoryAlerts.low > 0 || inventoryAlerts.out > 0) && (
          <div data-testid="menu-inventory-banner" className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3.5 rounded-xl border bg-amber-500/5 border-amber-500/15">
            <div className="flex items-center gap-2.5 flex-1">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
              </div>
              <div className="text-sm">
                {inventoryAlerts.out > 0 && (
                  <span className="font-semibold text-red-500">{t('crossSection.outOfStockAlert', { count: inventoryAlerts.out })}</span>
                )}
                {inventoryAlerts.out > 0 && inventoryAlerts.low > 0 && <span className="text-muted-foreground mx-1.5">•</span>}
                {inventoryAlerts.low > 0 && (
                  <span className="font-medium text-amber-600 dark:text-amber-400">{t('crossSection.lowStockAlert', { count: inventoryAlerts.low })}</span>
                )}
              </div>
            </div>
            <Link to="/dashboard/operations/inventory">
              <Button variant="outline" size="sm" className="rounded-full text-xs gap-1.5 border-amber-500/20 hover:bg-amber-500/10" data-testid="menu-go-inventory-btn">
                <Package className="w-3.5 h-3.5" /> {t('crossSection.goToInventory')} <ArrowRight className="w-3 h-3" />
              </Button>
            </Link>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('menu.searchPlaceholder')} className="pl-10" data-testid="menu-search-input" />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-[160px]" data-testid="menu-filter-category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('menu.allCategories')}</SelectItem>
              {categories.map(c => <SelectItem key={c.value} value={c.value}>{t(`menu.${c.value}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Items Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <UtensilsCrossed className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{t('menu.noItemsFound')}</p>
            <p className="text-xs mt-1">{t('menu.noItems')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(item => {
              const stockStatus = item.inventory_count <= 0 ? 'out' : item.inventory_count <= 5 ? 'low' : item.inventory_count <= 10 ? 'warn' : 'ok';
              return (
              <Card key={item.id} className="bg-card border border-border/50 rounded-xl card-hover overflow-hidden group" data-testid={`menu-item-${item.id}`}>
                {item.image_url ? (
                  <div className="h-32 overflow-hidden relative group/img">
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" referrerPolicy="no-referrer" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAIGenerateImage(item.id); }}
                      disabled={generatingImageFor === item.id}
                      data-testid={`ai-regen-image-${item.id}`}
                      className="absolute bottom-2 start-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 hover:bg-black/80 text-white text-[10px] font-medium transition-all opacity-0 group-hover/img:opacity-100 disabled:opacity-70"
                    >
                      {generatingImageFor === item.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Sparkles className="w-3 h-3" />}
                      {generatingImageFor === item.id ? t('menu.generatingImage') : t('menu.aiGenerateImage')}
                    </button>
                    {stockStatus === 'out' && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-red-500/90 text-white text-[10px] font-bold" data-testid={`stock-badge-${item.id}`}>
                        {t('inventory.outOfStock')}
                      </div>
                    )}
                    {stockStatus === 'low' && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-amber-500/90 text-white text-[10px] font-bold" data-testid={`stock-badge-${item.id}`}>
                        {t('inventory.lowStock')}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-20 bg-muted/30 flex items-center justify-center gap-3">
                    <UtensilsCrossed className="w-6 h-6 text-muted-foreground/20" />
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAIGenerateImage(item.id); }}
                      disabled={generatingImageFor === item.id}
                      data-testid={`ai-gen-image-${item.id}`}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 hover:bg-primary/20 text-primary text-[11px] font-medium transition-all disabled:opacity-50"
                    >
                      {generatingImageFor === item.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Sparkles className="w-3 h-3" />}
                      {generatingImageFor === item.id ? t('menu.generatingImage') : t('menu.aiGenerateImage')}
                    </button>
                  </div>
                )}
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-foreground text-sm">{item.name}</h3>
                      <p className="text-xs text-muted-foreground" dir="rtl">{item.name_ar}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">{catLabel(item.category)}</Badge>
                  </div>
                  {item.description && <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{item.description}</p>}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-primary">{item.price} {t('common.sar')}</span>
                      <span className={`text-xs font-medium ${stockStatus === 'out' ? 'text-red-500' : stockStatus === 'low' ? 'text-amber-500' : 'text-muted-foreground'}`}>
                        {t('menu.stock')}: {item.inventory_count}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-item-${item.id}`} className="h-7 w-7 p-0">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} data-testid={`delete-item-${item.id}`} className="h-7 w-7 p-0 text-destructive hover:text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
      </div>
    </ModuleLayout>
  );
}
